import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Link,
  Paper,
  IconButton,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  Pagination,
  Tabs,
  Tab
} from '@mui/material';
import {
  School as SchoolIcon,
  LocationOn,
  Language,
  People,
  AttachMoney,
  SportsSoccer,
  Event,
  School as ProgramIcon,
  Star,
  StarBorder,
} from '@mui/icons-material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';

// X (Twitter) SVG icon
const XIcon = (props: any) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
  </svg>
);
import { School } from '../../types/index';
import { usePrograms } from '../../contexts/ProgramsContext';
import { useSchoolsUS } from '../../contexts/SchoolsContextUS';
import { useEvents } from '../../contexts/EventsContext';
import { useUser } from '../../contexts/UserContext';
import { useAuthState } from '../../hooks/useAuthState';
import { ref as dbRef, get as dbGet, set as dbSet, remove as dbRemove } from 'firebase/database';
import { database, auth } from '../../firebase';
import { activityLogService } from '../../services/activityLogService';
import { calculateDistance, formatDistance } from '../../utils/distance';
import { getHighSchoolLocation, getHighSchoolLocationByName } from '../../utils/highSchoolLocation';
import MapModal from '../MapModal';

function formatWebsiteUrl(url?: string): string {
  if (!url) return '';
  
  // If the URL already has a protocol, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Otherwise, add https://
  return `https://${url}`;
}

const SchoolProfileUS: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { schools, loading: schoolsLoading, error: schoolsError } = useSchoolsUS();
  const { user, isAuthStateStable } = useAuthState();
  
  // Debug: Log when component mounts and schools data
  useEffect(() => {
    console.log('🔄 SchoolProfileUS: Component mounted, schools count:', schools.length);
    if (schools.length > 0) {
      console.log('🔍 SchoolProfileUS: First school data:', schools[0]);
    }
  }, [schools]);

  const { events: allEvents, loading: eventsLoading } = useEvents();
  const { 
    programs, 
    loading: programsLoading, 
    error: programsError,
    eligibilityLoading,
    eligibilityError,
    calculateEligibilityForSchools,
    isEligibilityCalculatedForSchools,
    getProgramEligibility,
    clearEligibility
  } = usePrograms();
  const { user: currentUser } = useUser();

  // Helper function to get the correct spelling based on user's country
  const getFavouritesLabel = () => {
    return currentUser?.country === 'US' ? 'Favorites' : 'Favourites';
  };
  

  // Removed: useScholarships, scholarships, scholarshipsLoading, scholarshipsError
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Removed pagination for programs - all programs will show on one page
  // Removed: scholarshipsPage, SCHOLARSHIPS_PER_PAGE
  const [selectedTab, setSelectedTab] = useState(() => {
    // Check if we're returning from EventDetail with a specific tab
    if (location.state?.selectedTab !== undefined) {
      return location.state.selectedTab;
    }
    // Check for tab query parameter
    const tabParam = searchParams.get('tab');
    if (tabParam === 'events') return 1;
    if (tabParam === 'programs') return 0;
    return 0;
  });

  // Handle location state changes for tab selection
  useEffect(() => {
    if (location.state?.selectedTab !== undefined) {
      setSelectedTab(location.state.selectedTab);
      // Clear the state to prevent it from persisting
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Handle URL parameter changes for tab selection
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'events') setSelectedTab(1);
    else if (tabParam === 'programs') setSelectedTab(0);
  }, [searchParams]);
  const [userSchoolName, setUserSchoolName] = useState('');
  const [athletics, setAthletics] = useState<{ sport: string; gender: string }[]>([]);
  const [athleticsLoading, setAthleticsLoading] = useState(false);
  const [distance, setDistance] = useState<number | null>(null);
  const [distanceLoading, setDistanceLoading] = useState(false);

  // Find the school by ID
  const school = useMemo(() => {
    if (!schools || !id) return null;
    return schools.find(s => s.id === id) || null;
  }, [schools, id]);

  // Get programs for this school
  const schoolPrograms = useMemo(() => {
    if (!programs || !school) return [];
    return programs.filter(p => p.schoolName === school.name);
  }, [programs, school]);

  // Get events for this school
  const schoolEvents = useMemo(() => {
    if (!allEvents || !school) return [];
    return allEvents.filter(e => e.host === school.name);
  }, [allEvents, school]);

  // State for managing favorites
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // Map modal state
  const [mapModalOpen, setMapModalOpen] = useState(false);

  // Check if school is favorite when component mounts or school changes
  const checkIfFavorite = useCallback(async () => {
    if (!school || !user || !isAuthStateStable) {
      setIsFavorite(false);
      return;
    }

    try {
      const favoriteRef = dbRef(database, `favoriteSchoolProgram/${school.name}/${school.name}/${user.uid}`);
      const snapshot = await dbGet(favoriteRef);
      setIsFavorite(snapshot.exists());
    } catch (error) {
      console.error('Error checking favorite status:', error);
      setIsFavorite(false);
    }
  }, [school, user, isAuthStateStable]);

  // Calculate distance from user's high school to this school
  const calculateDistanceFromUserSchool = useCallback(async () => {
    if (!currentUser || !school) return;
    
    setDistanceLoading(true);
    try {
      let userHighSchoolLocation = null;
      
      // Try to get location by ID first
      if (currentUser.schoolID) {
        userHighSchoolLocation = await getHighSchoolLocation(currentUser.schoolID);
      }
      
      // If not found by ID, try by name
      if (!userHighSchoolLocation && currentUser.schoolName) {
        userHighSchoolLocation = await getHighSchoolLocationByName(currentUser.schoolName);
      }
      
      if (userHighSchoolLocation && school.SchoolLocationLatitude && school.SchoolLocationLongitude) {
        const schoolLat = parseFloat(school.SchoolLocationLatitude);
        const schoolLon = parseFloat(school.SchoolLocationLongitude);
        
        if (schoolLat && schoolLon) {
          const calculatedDistance = calculateDistance(
            userHighSchoolLocation.latitude,
            userHighSchoolLocation.longitude,
            schoolLat,
            schoolLon
          );
          setDistance(calculatedDistance);
        }
      }
    } catch (error) {
      console.error('Error calculating distance:', error);
    } finally {
      setDistanceLoading(false);
    }
  }, [currentUser, school]);

  // Fetch athletics from database
  const fetchAthletics = useCallback(async () => {
    if (!school) return;
    setAthleticsLoading(true);
    try {
      const athleticsRef = dbRef(database, 'athletics_US');
      const athleticsSnap = await dbGet(athleticsRef);
      if (athleticsSnap.exists()) {
        const athleticsData = athleticsSnap.val();
        const filtered = Object.values(athleticsData).filter((a: any) => a.AthleticsSchoolName === school.name);
        setAthletics(filtered.map((a: any) => ({
          sport: a.AthleticsSport,
          gender: a.AthleticsGender === 'M' ? 'Male'
                : a.AthleticsGender === 'W' ? 'Female'
                : a.AthleticsGender // fallback for 'Both' or other values
        })));
      } else {
        setAthletics([]);
      }
    } catch (error) {
      console.error('Error fetching athletics:', error);
      setAthletics([]);
    } finally {
      setAthleticsLoading(false);
    }
  }, [school]);

  // Toggle favorite status
  const toggleFavorite = async () => {
    if (!school || !user || favoriteLoading) return;

    setFavoriteLoading(true);
    try {
      const favoriteRef = dbRef(database, `favoriteSchoolProgram/${school.name}/${school.name}/${user.uid}`);
      
      if (isFavorite) {
        await dbRemove(favoriteRef);
        setIsFavorite(false);
      } else {
        await dbSet(favoriteRef, {
          schoolName: school.name,
          timestamp: Date.now()
        });
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Check favorite status, calculate distance, and fetch athletics when school loads and auth state is stable
  useEffect(() => {
    if (school && !schoolsLoading) {
      checkIfFavorite();
      calculateDistanceFromUserSchool();
      fetchAthletics();
    }
  }, [school, schoolsLoading, currentUser?.schoolID, currentUser?.schoolName, user, isAuthStateStable, checkIfFavorite, calculateDistanceFromUserSchool, fetchAthletics]);

  // Calculate eligibility when school and user data are available
  useEffect(() => {
    if (school && currentUser && !schoolsLoading) {
      // Only calculate if we have user courses and haven't calculated yet
      if (Object.keys(currentUser.courses || {}).length > 0 && !isEligibilityCalculatedForSchools) {
        calculateEligibilityForSchools([school.name]);
      }
    }
  }, [school?.id, currentUser?.uid, schoolsLoading, currentUser?.courses, calculateEligibilityForSchools, isEligibilityCalculatedForSchools]);

  // Set loading state based on contexts
  useEffect(() => {
    setLoading(schoolsLoading || programsLoading || eventsLoading);
    setError(schoolsError || programsError || eligibilityError);
  }, [schoolsLoading, programsLoading, eventsLoading, schoolsError, programsError, eligibilityError]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!school) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">School not found</Alert>
      </Box>
    );
  }

  const handleBackNavigation = () => {
    // Check if we came from guidance dashboard
    const fromGuidance = searchParams.get('fromGuidance');
    if (fromGuidance === '1') {
      // Navigate back to guidance dashboard
      navigate('/guidance-dashboard');
      return;
    }
    
    navigate('/schools-us');
  };

  // Social media click handlers
  const handleSchoolWebsiteClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (school?.website && currentUser?.uid) {
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging website referral:', error);
      }
    }
    
    if (school?.website) {
      window.open(formatWebsiteUrl(school.website), '_blank', 'noopener,noreferrer');
    }
  };

  const handleSchoolCatalogClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (school?.SchoolCatalog && currentUser?.uid) {
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging catalog referral:', error);
      }
    }
    
    if (school?.SchoolCatalog) {
      window.open(formatWebsiteUrl(school.SchoolCatalog), '_blank', 'noopener,noreferrer');
    }
  };

  const handleVirtualTourClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (school?.SchoolVirtualTourURL && currentUser?.uid) {
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging virtual tour referral:', error);
      }
    }
    
    if (school?.SchoolVirtualTourURL) {
      window.open(formatWebsiteUrl(school.SchoolVirtualTourURL), '_blank', 'noopener,noreferrer');
    }
  };

  const handleCampusTourClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (school?.SchoolCampusTourURL && currentUser?.uid) {
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging campus tour referral:', error);
      }
    }
    
    if (school?.SchoolCampusTourURL) {
      window.open(formatWebsiteUrl(school.SchoolCampusTourURL), '_blank', 'noopener,noreferrer');
    }
  };

  const handleSchoolTwitterClick = async () => {
    if (school?.SchoolTwitterURL && currentUser?.uid) {
      try {
        await activityLogService.logExternalSchoolTwitterReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging Twitter referral:', error);
      }
    }
    
    if (school?.SchoolTwitterURL) {
      window.open(school.SchoolTwitterURL, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSchoolInstagramClick = async () => {
    if (school?.SchoolInstagramURL && currentUser?.uid) {
      try {
        await activityLogService.logExternalSchoolInstagramReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging Instagram referral:', error);
      }
    }
    
    if (school?.SchoolInstagramURL) {
      window.open(school.SchoolInstagramURL, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSchoolWikipediaClick = async () => {
    if (school?.SchoolWikiURL && currentUser?.uid) {
      try {
        await activityLogService.logExternalSchoolWikipediaReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging Wikipedia referral:', error);
      }
    }
    
    if (school?.SchoolWikiURL) {
      window.open(school.SchoolWikiURL, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSchoolYouTubeClick = async () => {
    if (school?.SchoolYoutubeURL && currentUser?.uid) {
      try {
        await activityLogService.logExternalSchoolYouTubeReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging YouTube referral:', error);
      }
    }
    
    if (school?.SchoolYoutubeURL) {
      window.open(school.SchoolYoutubeURL, '_blank', 'noopener,noreferrer');
    }
  };

  const renderPrograms = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Programs ({schoolPrograms.length})
      </Typography>
      
      {schoolPrograms.length === 0 ? (
        <Alert severity="info">No programs available for this school.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {schoolPrograms.map((program) => (
            <Card key={program.id}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {program.program}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {program.programCode}
                </Typography>
                {program.description && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {program.description}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/programs/${program.id}`)}
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );

  const renderEvents = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Events ({schoolEvents.length})
      </Typography>
      
      {schoolEvents.length === 0 ? (
        <Alert severity="info">No upcoming events for this school.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {schoolEvents.map((event) => (
            <Card key={event.id}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {event.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {event.date && new Date(event.date).toLocaleDateString()} • {event.location}
                </Typography>
                {event.description && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {event.description}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/events/${event.id}`)}
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', lg: 'row' } }}>
      {/* Main Content */}
      <Box sx={{ flex: 2 }}>
        <Button variant="outlined" sx={{ mb: 3 }} onClick={handleBackNavigation}>
          &larr; Back
        </Button>

        {/* School Header */}
        <Paper sx={{ p: { xs: 3, sm: 3 }, mb: 2 }}>
          {/* School Image - Full Width with Overlaid Logo */}
          <Box sx={{ mb: 3, position: 'relative' }}>
            <img
              src={school.image}
              alt={school.name}
              style={{ 
                width: '100%', 
                height: isMobile ? 200 : 250, 
                objectFit: 'cover', 
                borderRadius: 8,
                display: 'block'
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'https://via.placeholder.com/800x250/1976d2/ffffff?text=School+Image';
              }}
            />
            {/* Results Image (School Logo) - Overlaid */}
            {school.resultsImage && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -30,
                  left: 20,
                  width: isMobile ? 80 : 100,
                  height: isMobile ? 80 : 100,
                  borderRadius: '50%',
                  border: '4px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  overflow: 'hidden',
                  backgroundColor: 'white'
                }}
              >
                <img
                  src={school.resultsImage}
                  alt={`${school.name} Logo`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://via.placeholder.com/100x100/1976d2/ffffff?text=Logo';
                  }}
                />
              </Box>
            )}
          </Box>
          
          {/* School Name and Details */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            mb: 3, 
            gap: 2, 
            flexWrap: 'nowrap',
            mt: school.resultsImage ? (isMobile ? 6 : 7) : 0
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 2, 
                mb: 0.5, 
                flexWrap: 'nowrap'
              }}>
                <Typography 
                  variant={isMobile ? 'h5' : 'h4'} 
                  sx={{ 
                    mb: 0,
                    fontSize: isMobile ? '1.5rem' : '2.125rem',
                    lineHeight: isMobile ? '1.2' : '1.235',
                    wordBreak: 'break-word',
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  {school.name}
                </Typography>
                <Tooltip title={isFavorite ? `Remove from ${getFavouritesLabel().toLowerCase()}` : `Add to ${getFavouritesLabel().toLowerCase()}`}>
                  <IconButton 
                    onClick={toggleFavorite}
                    disabled={favoriteLoading}
                    sx={{ 
                      color: isFavorite ? '#8dd439' : 'grey.400',
                      flexShrink: 0,
                      '&:hover': {
                        color: isFavorite ? '#7bc536' : 'grey.600',
                      }
                    }}
                  >
                    {favoriteLoading ? (
                      <CircularProgress size={36} />
                    ) : isFavorite ? (
                      <Star sx={{ fontSize: '36px' }} />
                    ) : (
                      <StarBorder sx={{ fontSize: '36px' }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ wordBreak: 'break-word' }}>
                {school.SchoolLocationLatitude && school.SchoolLocationLongitude ? (
                  <Link
                    component="button"
                    variant="body1"
                    onClick={() => setMapModalOpen(true)}
                    sx={{
                      color: 'text.secondary',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    {school.city}, {school.province}
                  </Link>
                ) : (
                  `${school.city}, ${school.province}`
                )}
                {distance !== null && (
                  <span style={{ marginLeft: 12, color: 'inherit' }}>
                    &bull; {distanceLoading ? (
                      <CircularProgress size={12} sx={{ verticalAlign: 'middle', ml: 0.5 }} />
                    ) : (
                      `${formatDistance(distance, true)} away`
                    )}
                  </span>
                )}
                {school.SchoolPhone && (
                  <span style={{ marginLeft: 12, color: 'inherit' }}>
                    &bull; {school.SchoolPhone}
                  </span>
                )}
              </Typography>

              {/* School Stats and Social Links */}
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                flexWrap: 'wrap', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mt: 1
              }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                  <Chip label={`${Number(school.enrollment).toLocaleString()} students`} size="small" />
                  {school.website && (
                    <Chip 
                      label="Website" 
                      size="small" 
                      clickable 
                      onClick={handleSchoolWebsiteClick}
                    />
                  )}
                  {school.SchoolCatalog && (
                    <Chip 
                      label="Viewbook" 
                      size="small" 
                      clickable 
                      onClick={handleSchoolCatalogClick}
                    />
                  )}
                  {school.SchoolVirtualTourURL && (
                    <Chip 
                      label="View Virtual Tour" 
                      size="small" 
                      clickable 
                      onClick={handleVirtualTourClick}
                    />
                  )}
                  {school.SchoolCampusTourURL && (
                    <Chip 
                      label="Book a Campus Tour" 
                      size="small" 
                      clickable 
                      onClick={handleCampusTourClick}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  {school.SchoolTwitterURL && (
                    <Tooltip title="X (Twitter)">
                      <IconButton onClick={handleSchoolTwitterClick} sx={{ color: 'primary.main' }}>
                        <XIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {school.SchoolInstagramURL && (
                    <Tooltip title="Instagram">
                      <IconButton onClick={handleSchoolInstagramClick} sx={{ color: 'primary.main' }}>
                        <InstagramIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {school.SchoolWikiURL && (
                    <Tooltip title="Wikipedia">
                      <IconButton onClick={handleSchoolWikipediaClick} sx={{ color: 'primary.main' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm.01 3.5c.82 0 1.49.67 1.49 1.5s-.67 1.5-1.49 1.5c-.82 0-1.49-.67-1.49-1.5s.67-1.5 1.49-1.5zm2.99 13h-2.01v-1.5h2.01V18.5zm2.5-2.5h-1.5v-1.5h1.5V16zm-10 0H5.5v-1.5H7V16zm2.5-2.5H7v-1.5h2.5V13.5zm5 0h-2.5v-1.5H17V13.5zm-2.5-2.5h-2.01V9.5h2.01V11zm-2.5-2.5H7V7.5h2.5V8.5zm5 0h-2.5V7.5H17V8.5z" fill="#000"/></svg>
                      </IconButton>
                    </Tooltip>
                  )}
                  {school.SchoolYoutubeURL && (
                    <Tooltip title="YouTube">
                      <IconButton onClick={handleSchoolYouTubeClick} sx={{ color: 'primary.main' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M21.8 8.001a2.75 2.75 0 0 0-1.94-1.94C18.2 6 12 6 12 6s-6.2 0-7.86.06A2.75 2.75 0 0 0 2.2 8.001C2.14 9.66 2.14 12 2.14 12s0 2.34.06 3.999a2.75 2.75 0 0 0 1.94 1.94C5.8 18 12 18 12 18s6.2 0 7.86-.06a2.75 2.75 0 0 0 1.94-1.94c.06-1.66.06-3.999.06-3.999s0-2.34-.06-3.999zM9.75 15.02V8.98l6.5 3.02-6.5 3.02z" fill="#f00"/></svg>
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {/* School Description */}
              {school.description && (
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {school.description}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Tabs for main sections */}
        <Paper sx={{ p: 2, mb: 4 }}>
          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 3 }}
          >
            <Tab label="Programs" />
            <Tab label="Events" />
          </Tabs>
          
          {/* Programs Tab */}
          {selectedTab === 0 && renderPrograms()}
          
          {/* Events Tab */}
          {selectedTab === 1 && renderEvents()}
        </Paper>
      </Box>

      {/* Sidebar */}
      <Box sx={{ flex: 1, minWidth: 300 }}>
        {/* Acceptance Rate */}
        {school.acceptanceRate !== undefined && school.acceptanceRate !== null && school.acceptanceRate > 0 && (
          <Paper sx={{ p: 3, mb: 4, mt: { lg: 7.5 } }}>
            <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>Acceptance Rate</Typography>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 700, textAlign: 'center' }}>
              {Number(school.acceptanceRate).toLocaleString()}%
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'left', mt: 1 }}>
              Admission requirements for each individual program will vary. For a more accurate estimate, please refer to the program requirements.
            </Typography>
          </Paper>
        )}

        {/* General Admission Requirements Section */}
        {school.admissionRequirements && school.admissionRequirements.length > 0 && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>General Admission Requirements</Typography>
            <Typography variant="body1" color="text.secondary">
              {Array.isArray(school.admissionRequirements)
                ? school.admissionRequirements.join(' ')
                : school.admissionRequirements}
            </Typography>
          </Paper>
        )}

        {/* Admissions Minimum Requirements */}
        {school.admissionsMinimum && school.admissionsMinimum.trim() !== '' && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>Admissions Minimum Requirements</Typography>
            <Typography variant="body1" color="text.secondary">
              {school.admissionsMinimum}
            </Typography>
          </Paper>
        )}

        {/* Varsity Athletics */}
        {!athleticsLoading && athletics.length > 0 && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Varsity Athletics{school.athleticsNickname ? ` (${school.athleticsNickname})` : ''}
            </Typography>
            {school.athleticsWebsite && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label="Website" 
                  clickable
                  onClick={() => window.open(school.athleticsWebsite, '_blank')}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
              {/* Male Column */}
              <Box sx={{ flex: 1, bgcolor: 'grey.50', borderRadius: 2, p: 2, boxShadow: 1, minWidth: 140 }}>
                {(() => {
                  const maleSports = athletics.filter(a => a.gender === 'Male').map(a => a.sport).sort();
                  return (
                    <Typography variant="subtitle2" gutterBottom align="left" sx={{ fontWeight: 600, letterSpacing: 1 }}>
                      Male{maleSports.length > 0 ? ` (${maleSports.length})` : ''}
                    </Typography>
                  );
                })()}
                {(() => {
                  const maleSports = athletics.filter(a => a.gender === 'Male').map(a => a.sport).sort();
                  return maleSports.length > 0 ? (
                    <List dense sx={{ pl: 2 }}>
                      {maleSports.map((sport, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5, px: 0 }} disableGutters>
                          <ListItemText primary={sport} primaryTypographyProps={{ fontSize: 16 }} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center">No male sports.</Typography>
                  );
                })()}
              </Box>
              {/* Female Column */}
              <Box sx={{ flex: 1, bgcolor: 'grey.50', borderRadius: 2, p: 2, boxShadow: 1, minWidth: 140 }}>
                {(() => {
                  const femaleSports = athletics.filter(a => a.gender === 'Female').map(a => a.sport).sort();
                  return (
                    <Typography variant="subtitle2" gutterBottom align="left" sx={{ fontWeight: 600, letterSpacing: 1 }}>
                      Female{femaleSports.length > 0 ? ` (${femaleSports.length})` : ''}
                    </Typography>
                  );
                })()}
                {(() => {
                  const femaleSports = athletics.filter(a => a.gender === 'Female').map(a => a.sport).sort();
                  return femaleSports.length > 0 ? (
                    <List dense sx={{ pl: 2 }}>
                      {femaleSports.map((sport, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5, px: 0 }} disableGutters>
                          <ListItemText primary={sport} primaryTypographyProps={{ fontSize: 16 }} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center">No female sports.</Typography>
                  );
                })()}
              </Box>
            </Box>
          </Paper>
        )}
        
        {/* Debug: Show admissionsMinimum value */}
        {process.env.NODE_ENV === 'development' && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'yellow', fontSize: '12px' }}>
            <Typography variant="caption">
              Debug - admissionsMinimum: "{school.admissionsMinimum}" (type: {typeof school.admissionsMinimum})
            </Typography>
          </Paper>
        )}

      </Box>

      {/* Map Modal */}
      <MapModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        eventName={school.name}
        latitude={school.SchoolLocationLatitude}
        longitude={school.SchoolLocationLongitude}
      />
    </Box>
  );
};

export default SchoolProfileUS;
